import type {
  AgingColumn,
  DueScheduleCurrencyBlock,
  DueSchedulePeriod,
  SupplierBalancesReport,
} from '../../lib/accountsPayableReportsApi';
import { payableListHref, type PayableFilters } from './payableFilters';

/**
 * Where a report figure links to: the bill list, filtered to EXACTLY the bills that figure adds
 * up. The reports (server) decide what a column holds; this file only turns that decision into
 * the list's filters. Every due-day range comes from the server's own answer (`dueFrom` /
 * `dueTo` on an aging column, a schedule period's `start` / `end`, `overdueThrough`), so no day
 * is counted in the browser.
 *
 * Three constants recur, because the reports' columns are built the same way:
 *   - a report only counts bills that still owe something   -> `openOnly`
 *   - a dated column leaves out bills on hold or disputed   -> `held: 'exclude'`
 *   - the "on hold or disputed" column is exactly those     -> `held: 'only'`
 */

/** A supplier row of a report is one stored name, exactly; a currency total has no supplier. */
export interface DrillScope {
  currency: string;
  /** The stored supplier name, or null for a whole-currency figure. */
  supplierName: string | null;
}

function scopeFilters(scope: DrillScope): Partial<PayableFilters> {
  return {
    currency: scope.currency,
    openOnly: true,
    ...(scope.supplierName === null ? {} : { supplier: scope.supplierName, supplierExact: true }),
  };
}

/** A cell of the aging report: one column, for one supplier or for the whole currency. */
export function agingCellHref(scope: DrillScope, column: AgingColumn): string {
  const base = scopeFilters(scope);
  if (column.key === 'total') return payableListHref(base);
  if (column.key === 'on_hold_disputed') return payableListHref({ ...base, held: 'only' });
  if (column.key === 'no_due_date') return payableListHref({ ...base, held: 'exclude', due: 'no_due_date' });
  return payableListHref({
    ...base,
    held: 'exclude',
    dueFrom: column.dueFrom ?? '',
    dueTo: column.dueTo ?? '',
  });
}

/** A period of the due schedule (a day, or a Monday-to-Sunday week clipped to the range). */
export function schedulePeriodHref(currency: string, period: DueSchedulePeriod): string {
  return payableListHref({
    currency,
    openOnly: true,
    held: 'exclude',
    dueFrom: period.start,
    dueTo: period.end,
  });
}

export type ScheduleSlot = 'carriedOver' | 'noDueDate' | 'onHoldDisputed' | 'afterRange' | 'owed';

/** One of the non-period columns of the due schedule. */
export function scheduleSlotHref(block: DueScheduleCurrencyBlock, slot: ScheduleSlot): string {
  const base = { currency: block.currency, openOnly: true } as const;
  switch (slot) {
    case 'carriedOver':
      return payableListHref({ ...base, held: 'exclude', dueTo: block.carriedOver.dueTo });
    case 'noDueDate':
      return payableListHref({ ...base, held: 'exclude', due: 'no_due_date' });
    case 'onHoldDisputed':
      return payableListHref({ ...base, held: 'only' });
    case 'afterRange':
      return payableListHref({ ...base, held: 'exclude', dueFrom: block.afterRange.dueFrom });
    default:
      return payableListHref(base);
  }
}

export type BalanceFigure = 'owed' | 'overdue' | 'onHoldDisputed';

/** A figure on a supplier's row of the supplier-balances report. */
export function balanceFigureHref(
  report: Pick<SupplierBalancesReport, 'overdueThrough'>,
  scope: DrillScope,
  figure: BalanceFigure
): string {
  const base = scopeFilters(scope);
  if (figure === 'onHoldDisputed') return payableListHref({ ...base, held: 'only' });
  if (figure === 'overdue') return payableListHref({ ...base, held: 'exclude', dueTo: report.overdueThrough });
  return payableListHref(base);
}
